import { Popover as PopoverPrimitive } from '@base-ui/react'
import { ListFilter } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import type { ChoiceOption } from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* One facet of a list's filter — status, mode, owner — as a multi-select
 * (UX-DR-4).
 *
 * MULTI-SELECT, NOT SINGLE. The question a pipeline gets asked is "what is
 * still open", which is three statuses and not one, and a single-select facet
 * turns that into three passes over the list. Nothing selected means no
 * narrowing, which is why the trigger reads "All" rather than "None": an empty
 * selection is the whole list, and a control that said "None" next to a full
 * list would be lying.
 *
 * Like ColumnsMenu it OWNS NOTHING — `useViewState` remembers the selection and
 * the page applies it. This control renders it and reports changes.
 *
 * The clear affordance is not decoration. A filter the user has forgotten is
 * the single most common way a list "loses" a record, so the count is on the
 * trigger where it is visible without opening anything, and the way out is one
 * press once the menu is open.
 */

export interface FacetFilterProps {
  /** Names the facet — "Status". Defaults to the generic filter label. */
  label?: string
  options: readonly ChoiceOption[]
  /** Selected values. Empty means no narrowing. */
  selected: readonly string[]
  onChange: (values: string[]) => void
  className?: string
}

export function FacetFilter({
  label,
  options,
  selected,
  onChange,
  className,
}: FacetFilterProps) {
  const { t } = useTranslation()

  const name = label ?? t('common.filters.label')

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((held) => held !== value)
        : [...selected, value],
    )
  }

  return (
    <PopoverPrimitive.Root modal={false}>
      <PopoverPrimitive.Trigger
        render={<Button variant="secondary" size="sm" iconLeft={ListFilter} />}
        className={className}
      >
        {/* The summary carries the state onto the closed trigger. A separator
            colon rather than a badge, because the value it summarises is a
            phrase ("2 filters") and not a number — and the colon is what makes
            "Status All" read as a control with a value rather than as two
            words that happen to be next to each other. */}
        {name}:
        <span className="text-text-muted">
          {selected.length === 0
            ? t('common.filters.all')
            : t('common.filters.activeCount', { count: selected.length })}
        </span>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner sideOffset={4} align="start" className="z-[60]">
          <PopoverPrimitive.Popup
            aria-label={name}
            className={cn(
              'bg-surface border-border-subtle rounded-md border',
              'shadow-[var(--shadow-overlay)] w-[240px] p-2',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            )}
          >
            <ul className="asa-scroll flex max-h-[300px] flex-col gap-0.5 overflow-y-auto">
              {options.map((option) => (
                <li key={option.value}>
                  <Checkbox
                    className="px-2 py-1.5"
                    label={option.label}
                    hint={option.hint}
                    disabled={option.disabled}
                    checked={selected.includes(option.value)}
                    onCheckedChange={() => toggle(option.value)}
                  />
                </li>
              ))}
            </ul>

            <div className="border-line-hair mt-2 border-t pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={selected.length === 0}
                onClick={() => onChange([])}
              >
                {t('common.filters.clear')}
              </Button>
            </div>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

/** What a list shows when a filter has narrowed it to nothing.
 *
 *  Distinct from the surface's own empty state, and that distinction is the
 *  point: "you have no quotes" and "your filter matches no quotes" are
 *  different facts, and showing the first when the second is true tells the
 *  user their data is gone. It lives beside the control that causes it so the
 *  two cannot be worded apart, and it carries the way out. */
export function FilterEmptyState({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation()
  return (
    <EmptyState
      icon={ListFilter}
      title={t('common.filters.emptyTitle')}
      description={t('common.filters.emptyDescription')}
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          {t('common.filters.clear')}
        </Button>
      }
    />
  )
}
